import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Sparkles, Lock, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setValid(false);
        setValidating(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/reset-password/validate`, { params: { token } });
        setValid(!!res.data.valid);
        setEmail(res.data.email || '');
      } catch {
        setValid(false);
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password reset! Redirecting to sign in...');
      setTimeout(() => navigate('/login'), 1800);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">PostToSocial</span>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">
            {done ? 'Password reset' : valid ? 'Choose a new password' : 'Link unavailable'}
          </h1>
          {valid && !done && email && (
            <p className="text-zinc-500 mt-2">For <span className="font-medium text-zinc-900">{email}</span></p>
          )}
        </div>

        <Card className="border-zinc-200 shadow-sm animate-fade-in" style={{ animationDelay: '0.1s' }}>
          {!valid ? (
            <CardContent className="pt-6 pb-6 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-zinc-900 mb-2" data-testid="reset-invalid-title">
                This link is invalid or expired
              </h2>
              <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
                Reset links expire 1 hour after they're issued and can only be used once.
                Please request a new one.
              </p>
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={() => navigate('/forgot-password')}
                data-testid="request-new-link-btn"
              >
                Request a new link
              </Button>
            </CardContent>
          ) : done ? (
            <CardContent className="pt-6 pb-6 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-zinc-900 mb-2" data-testid="reset-success-title">
                All set!
              </h2>
              <p className="text-sm text-zinc-600">
                Your password has been updated. Redirecting to sign in...
              </p>
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">Set new password</CardTitle>
                <CardDescription>Use at least 8 characters</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">New password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10"
                        required
                        minLength={8}
                        data-testid="reset-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm">Confirm password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="confirm"
                        type="password"
                        placeholder="••••••••"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="pl-10"
                        required
                        minLength={8}
                        data-testid="reset-confirm-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Reset password
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        {valid && !done && (
          <p className="text-center text-sm text-zinc-500 mt-6">
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="reset-back-to-login">
              Back to sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};
